/**
 * Container + participant status display helpers — single source of
 * truth for the Arabic label and the Tailwind color tokens used to
 * render every status pill across the app. Adding a new status?
 * Update the enums in `api/containers.ts` first, then add it here.
 */
import type { ContainerStatus, ParticipantStatus } from '@/api/containers';

interface StatusDisplay {
  label: string;
  /** Tailwind utility classes — bg + text, e.g. `bg-success/10 text-success` */
  color: string;
}

const FALLBACK: StatusDisplay = { label: '—', color: 'bg-border/40 text-text-secondary' };

const CONTAINER: Record<ContainerStatus, StatusDisplay> = {
  draft:               { label: 'مسوّدة',           color: 'bg-text-tertiary/10 text-text-secondary' },
  open_for_joins:      { label: 'تسجيل مفتوح',     color: 'bg-info/10 text-info' },
  joins_closed:        { label: 'تسجيل مُغلق',      color: 'bg-warning/10 text-warning' },
  consolidating:       { label: 'تجميع البضائع',   color: 'bg-warning/10 text-warning' },
  sealed:              { label: 'مختومة',           color: 'bg-warning/10 text-warning' },
  in_transit:          { label: 'في الطريق',        color: 'bg-primary/10 text-primary' },
  at_destination_port: { label: 'في ميناء الوجهة', color: 'bg-primary/10 text-primary' },
  customs_filed:       { label: 'تقديم تخليص',     color: 'bg-warning/10 text-warning' },
  customs_cleared:     { label: 'تخليص مكتمل',      color: 'bg-success/10 text-success' },
  unloading:           { label: 'تفريغ',            color: 'bg-success/10 text-success' },
  completed:           { label: 'مكتملة',           color: 'bg-success/10 text-success' },
  cancelled:           { label: 'مُلغاة',            color: 'bg-error/10 text-error' },
  lost:                { label: 'مفقودة',           color: 'bg-error/10 text-error' },
  seized:              { label: 'مصادرة',           color: 'bg-error/10 text-error' },
};

const PARTICIPANT: Record<ParticipantStatus, StatusDisplay> = {
  join_requested:                   { label: 'طلب انضمام',           color: 'bg-info/10 text-info' },
  approved:                         { label: 'تمّت الموافقة',         color: 'bg-success/10 text-success' },
  awaiting_supplier_dispatch:       { label: 'بانتظار إرسال المورّد', color: 'bg-warning/10 text-warning' },
  pending_warehouse_confirmation:   { label: 'بانتظار تأكيد المستودع', color: 'bg-warning/10 text-warning' },
  at_origin_warehouse:              { label: 'وصلت مستودع المنشأ',   color: 'bg-success/10 text-success' },
  loaded_in_container:              { label: 'محمّلة في الحاوية',     color: 'bg-primary/10 text-primary' },
  sealed:                           { label: 'مختومة',                color: 'bg-warning/10 text-warning' },
  in_transit:                       { label: 'في الطريق',             color: 'bg-primary/10 text-primary' },
  at_destination_port:              { label: 'في ميناء الوجهة',       color: 'bg-primary/10 text-primary' },
  customs_filed_with_fee:           { label: 'تقديم تخليص + رسوم',    color: 'bg-warning/10 text-warning' },
  customs_cleared:                  { label: 'تخليص مكتمل',            color: 'bg-success/10 text-success' },
  arrived_at_destination_warehouse: { label: 'في مستودع الوجهة',      color: 'bg-success/10 text-success' },
  handed_to_final_mile:             { label: 'في التوصيل النهائي',    color: 'bg-success/10 text-success' },
  delivered:                        { label: 'تمّ التسليم',            color: 'bg-success/10 text-success' },
  rejected:                         { label: 'مرفوضة',                color: 'bg-error/10 text-error' },
  withdrawn:                        { label: 'مُلغاة من المرسل',      color: 'bg-error/10 text-error' },
  exception_pending:                { label: 'استثناء قيد المعالجة',   color: 'bg-error/10 text-error' },
  exception_terminal:               { label: 'استثناء نهائي',          color: 'bg-error/10 text-error' },
  refunded:                         { label: 'مُستردّة',               color: 'bg-text-tertiary/10 text-text-secondary' },
};

export function containerStatusDisplay(s?: string | null): StatusDisplay {
  if (!s) return FALLBACK;
  return CONTAINER[s as ContainerStatus] ?? { label: s, color: FALLBACK.color };
}

export function participantStatusDisplay(s?: string | null): StatusDisplay {
  if (!s) return FALLBACK;
  return PARTICIPANT[s as ParticipantStatus] ?? { label: s, color: FALLBACK.color };
}

/**
 * Approximate fill fraction (0..1) prioritising CBM, falling back to
 * weight. Mirrors Flutter `CargoContainer.fillFraction`.
 */
export function containerFillFraction(c: {
  max_cbm?: number | null; used_cbm?: number | null;
  max_weight_kg?: number | null; used_weight_kg?: number | null;
}): number {
  if (c.max_cbm && c.max_cbm > 0 && c.used_cbm != null) {
    return Math.min(1, Math.max(0, c.used_cbm / c.max_cbm));
  }
  if (c.max_weight_kg && c.max_weight_kg > 0 && c.used_weight_kg != null) {
    return Math.min(1, Math.max(0, c.used_weight_kg / c.max_weight_kg));
  }
  return 0;
}

/**
 * Human-readable price label for browse / detail headers.
 * Returns null if the container has no priced pricing mode.
 */
export function containerPriceLabel(c: {
  pricing_mode: string;
  price_per_kg_usd?: number | null;
  price_per_cbm_usd?: number | null;
  price_per_vehicle_usd?: number | null;
  pricing_mixed_rule?: string | null;
}): string | null {
  if (c.price_per_vehicle_usd != null) return `$${c.price_per_vehicle_usd} / vehicle`;
  switch (c.pricing_mode) {
    case 'per_kg':
      return c.price_per_kg_usd != null ? `$${c.price_per_kg_usd} / kg` : null;
    case 'per_cbm':
      return c.price_per_cbm_usd != null ? `$${c.price_per_cbm_usd} / m³` : null;
    case 'mixed': {
      const kg = c.price_per_kg_usd != null ? `$${c.price_per_kg_usd}/kg` : '—';
      const cbm = c.price_per_cbm_usd != null ? `$${c.price_per_cbm_usd}/m³` : '—';
      const op = c.pricing_mixed_rule === 'sum' ? '+' : '∨';
      return `${kg} ${op} ${cbm}`;
    }
    default:
      return null;
  }
}
